import { classifyGenerationFailure, getGenerationErrorDetails } from './generation-error-details.js';
import { getSubscriptionManagementTarget, getSubscriptionTierLabel } from './subscription-ui.js';
import { isRetryableResult } from './generation-retry.js';

const RECOVERABLE = new Set([
  'balance', 'spark-tier', 'entitlement', 'billing-recovery', 'plan-limit',
  'trial-limit', 'fair-use', 'payment-verification', 'free-limits', 'request-cost'
]);

/** Exclude successes and jobs whose server outcome is still unresolved. */
export function getRecoverableSlots(request, results = {}) {
  return (request?.slots || []).filter((slot) => isRetryableResult(results[slot.id]));
}

export function getCreditRecoveryView(error, { subscription, request, quote, results = {}, kind = 'images' } = {}) {
  const failure = classifyGenerationFailure(error);
  const details = getGenerationErrorDetails(error);
  const active = subscription?.active === true && ['unlimited', 'unlimited_pro'].includes(subscription.tier);
  const slots = getRecoverableSlots(request, results);
  const replaceModel = kind === 'images' && slots.length > 0
    && ['model-retired', 'model-unavailable'].includes(failure);
  const includedIntent = slots.length > 0
    ? slots.every((slot) => slot.projectConfig?.billingMode === 'subscription')
    : request?.requests?.length > 0
      ? request.requests.every(params => params.billingMode === 'subscription')
      : (request?.projectConfig || details.request?.projectConfig)?.billingMode === 'subscription';
  const allIncluded = active && includedIntent;
  const premiumOnly = failure === 'spark-tier' || Number(quote?.premiumTotal || 0) > 0;
  const management = getSubscriptionManagementTarget(subscription);
  const requiredPlans = Array.isArray(details.requiredPlans) ? details.requiredPlans : [];
  const nextPlans = requiredPlans.filter((plan) => plan !== subscription?.tier);
  const canBuyPlan = !active && !['billing-recovery', 'request-cost', 'fair-use'].includes(failure);
  const canUpgradePro = active && subscription.tier === 'unlimited'
    && nextPlans.includes('unlimited_pro') && ['plan-limit', 'fair-use'].includes(failure);
  const canActivatePaid = failure === 'trial-limit' && subscription?.status === 'trialing' && subscription?.provider === 'stripe';
  const hasIncludedModels = slots.some((slot) => !slot.partner);
  const showPlans = !replaceModel && (canBuyPlan || canUpgradePro || failure === 'payment-verification'
    || failure === 'trial-limit' || (failure === 'plan-limit' && nextPlans.length > 0));
  let title = failure === 'spark-tier' ? 'Premium Spark access required' : 'More Spark needed';
  let description = details.limitation || details.message || 'Review your balance and saved request before continuing.';
  if (replaceModel) {
    title = 'Choose a current image model';
    description = 'The saved model is retired or unavailable. Choose a current model for these failed or unsubmitted images, then review its settings and any credit charge. Your successful and unresolved images stay in place.';
  } else if (failure === 'entitlement' || (allIncluded && failure === 'balance')) {
    title = 'Check your Unlimited access';
    description = 'This request uses models included in your plan. Refresh your account and review access before retrying.';
  } else if (failure === 'billing-recovery') {
    title = 'Your subscription payment needs attention';
  } else if (failure === 'trial-limit') {
    title = 'Your free trial limit was reached';
  } else if (failure === 'fair-use') {
    title = 'Fast rendering capacity used';
  } else if (failure === 'plan-limit') {
    title = 'Review your plan for this request';
  } else if (failure === 'payment-verification') {
    title = 'Verify a payment method to use Free Spark';
  } else if (failure === 'free-limits') {
    title = 'This request exceeds Free Spark limits';
  } else if (failure === 'request-cost') {
    title = 'This request exceeds the render cost limit';
  }
  return {
    recoverable: RECOVERABLE.has(failure) || replaceModel, failure, details, title, description,
    tierLabel: getSubscriptionTierLabel(subscription), premiumOnly, allIncluded,
    slots, count: kind === 'images' ? slots.length : (request?.items?.length || 1),
    showTopUp: !replaceModel && failure !== 'request-cost' && !(allIncluded && ['balance', 'entitlement'].includes(failure)),
    showPlans, planLabel: canUpgradePro ? 'Review upgrade to Unlimited Pro' : active ? 'Review plan options' : 'Compare Unlimited plans',
    showProviderRecovery: failure === 'billing-recovery' || failure === 'entitlement', management,
    canActivatePaid,
    showReplacementModel: replaceModel,
    showCoveredSubset: !replaceModel && active && hasIncludedModels && slots.some((slot) => slot.partner),
    showCoveredModel: !replaceModel && kind === 'images' && active && !hasIncludedModels && slots.length > 0,
    showSmallerBatch: !replaceModel && kind === 'images' && slots.length > 1,
    showRelaxed: kind === 'images' && failure === 'fair-use'
  };
}
